import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Crown } from "lucide-react";
import { api } from "../lib/api";
import { TeacherCard, TeacherCardSkeleton } from "./TeacherCard";

export default function FeaturedCarousel() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/teachers/featured")
      .then(({ data }) => {
        if (!cancelled) setTeachers(data || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Hide entire section if not loading and no featured teachers — avoids empty hero
  if (!loading && teachers.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-card]");
    const step = card ? card.clientWidth + 20 : 320;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 mt-2 mb-2"
      data-testid="featured-carousel"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Crown className="h-6 w-6" style={{ color: "#FFD708" }} fill="#FFD708" />
            Featured Mentors
          </h2>
          <p className="text-sm mt-1" style={{ color: "#13A699AA" }}>
            Top-rated experts hand-picked for you
          </p>
        </div>
        {teachers.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="h-9 w-9 rounded-full bg-white border border-black/10 flex items-center justify-center hover:bg-[#FFF7ED]"
              aria-label="Scroll left"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="h-4 w-4" style={{ color: "#13A699" }} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="h-9 w-9 rounded-full bg-white border border-black/10 flex items-center justify-center hover:bg-[#FFF7ED]"
              aria-label="Scroll right"
              data-testid="carousel-next"
            >
              <ChevronRight className="h-4 w-4" style={{ color: "#13A699" }} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-5 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        data-testid="featured-carousel-scroller"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="snap-start shrink-0 w-[280px] sm:w-[300px]"
                data-carousel-card
              >
                <TeacherCardSkeleton />
              </div>
            ))
          : teachers.map((t) => (
              <div
                key={t.id}
                className="snap-start shrink-0 w-[280px] sm:w-[300px]"
                data-carousel-card
              >
                <TeacherCard teacher={t} />
              </div>
            ))}
      </div>
    </section>
  );
}
